<template>
<div>
    <div class="mb-3">
        <b-button v-if="canAdd" @click="toggleAdd" :variant="addNew ? 'danger' : 'primary'">
            <i :class="addNew ? 'fa-solid fa-ban' : 'fa-solid fa-plus'"></i> {{ addNew ? 'Cancel' : 'Add pregnancy' }}
        </b-button>
        <b-collapse v-model="addNew" class="mt-3">
            <b-card>
                <validation-observer ref="observer" v-slot="{ passes }">
                <b-form @submit.prevent="passes(onSubmit)" novalidate>
                    <b-row>
                        <b-col md="2">
                            <validation-provider vid="sequence_no" v-slot="{ errors }">
                                <b-form-group label="No." :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" min="0" v-model.number="form.sequence_no" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="4">
                            <validation-provider vid="date" v-slot="{ errors }">
                                <b-form-group label="Date" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-datepicker v-model="form.date" :locale="currentLang" :max="today" reset-button placeholder="Date..." />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="6">
                            <validation-provider vid="place" v-slot="{ errors }">
                                <b-form-group label="Place" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input v-model="form.place" placeholder="Place of delivery..." />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="4">
                            <validation-provider vid="delivery_type" v-slot="{ errors }">
                                <b-form-group label="Delivery type" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input v-model="form.delivery_type" placeholder="e.g. SVD, C-section..." />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="gender" v-slot="{ errors }">
                                <b-form-group label="Gender" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-select plain v-model="form.gender">
                                        <b-form-select-option :value="null">n/a</b-form-select-option>
                                        <b-form-select-option value="male">Male</b-form-select-option>
                                        <b-form-select-option value="female">Female</b-form-select-option>
                                        <b-form-select-option value="unknown">Unknown</b-form-select-option>
                                    </b-form-select>
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="2">
                            <validation-provider vid="weight" v-slot="{ errors }">
                                <b-form-group label="Weight (kg)" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input type="number" step="0.01" min="0" v-model.number="form.weight" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                        <b-col md="3">
                            <validation-provider vid="gestational_age" v-slot="{ errors }">
                                <b-form-group label="Gestational age" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                                    <b-form-input v-model="form.gestational_age" placeholder="e.g. 39w+2d" />
                                </b-form-group>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <validation-provider vid="notes" v-slot="{ errors }">
                        <b-form-group label="Notes" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-textarea rows="2" max-rows="6" v-model="form.notes" placeholder="Notes..." />
                        </b-form-group>
                    </validation-provider>

                    <b-button class="mr-1" type="submit" variant="success"><i class="fa-regular fa-circle-dot"></i> {{ editingId ? 'Save' : 'Submit' }}</b-button>
                    <b-button v-if="isDirty || hasErrors" type="button" variant="danger" @click="resetForm"><i class="fa-solid fa-ban"></i> Reset</b-button>
                </b-form>
                </validation-observer>
            </b-card>
        </b-collapse>
    </div>

    <b-overlay :show="confirm" no-wrap @shown="onShown">
        <template #overlay>
            <div ref="dialog" tabindex="-1" role="dialog" aria-modal="false">
                <p class="text-center">
                    <i class="fa-light fa-xmark text-danger"></i> <strong class="text-danger">DELETE pregnancy entry?</strong>
                </p>
                <div class="text-center">
                    <b-button variant="outline-danger" class="mr-3" @click="onCancel">Cancel</b-button>
                    <b-button variant="outline-success" @click="onConfirmed(selected)">Confirm</b-button>
                </div>
            </div>
        </template>
    </b-overlay>

    <b-table
        responsive
        stacked="sm"
        show-empty
        striped
        hover
        small
        primary-key="id"
        :current-page="currentPage"
        :per-page="perPage"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortedBy"
        :sort-desc.sync="sortedDesc">

        <template v-slot:cell(date)="data">
            <span :class="{ 'small font-italic text-muted': !data.value }">{{ data.value ? toLocaleDate(data.value) : 'n/a' }}</span>
        </template>
        <template v-slot:cell(actions)="row">
            <b-button v-if="canChange" size="sm" variant="outline-primary" class="mr-1" @click="onEdit(row.item)"><i class="fa-light fa-pen"></i></b-button>
            <b-button v-if="canDelete" size="sm" variant="danger" @click="onRemove(row.item)"><i class="fa-light fa-xmark"></i></b-button>
        </template>
        <template v-slot:empty>
            <div class="small text-muted text-center my-2"><span>No previous pregnancies recorded</span></div>
        </template>
    </b-table>
    <b-pagination v-if="totalRows > perPage" class="m-0" align="right" :total-rows="totalRows" v-model="currentPage" :per-page="perPage" />
</div>
</template>
<script>
import FormExtension from "@/core/components/FormExtension"
import { mapActions } from "vuex"

export default FormExtension.extend({
    name: "pregnancy-history-list",
    props: {
        record: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            today: new Date(),
            addNew: false,
            editingId: null,
            confirm: false,
            selected: null,
            currentPage: 1,
            perPage: 5,
            sortedBy: 'sequence_no',
            sortedDesc: false,
            form: new Form({
                sequence_no: null,
                date: null,
                place: "",
                delivery_type: "",
                gender: null,
                weight: null,
                gestational_age: "",
                notes: "",
                consistency_token: null,
            }),
            fields: [
                { key: 'sequence_no', label: 'No.', class: 'align-middle' },
                { key: 'date', label: 'Date', class: 'align-middle' },
                { key: 'place', label: 'Place', class: 'align-middle' },
                { key: 'delivery_type', label: 'Delivery', class: 'align-middle' },
                { key: 'gender', label: 'Gender', class: 'align-middle text-capitalize' },
                { key: 'weight', label: 'Weight', class: 'align-middle' },
                { key: 'gestational_age', label: 'GA', class: 'align-middle' },
                { key: 'actions', label: '', class: 'align-middle text-right' },
            ],
        }
    },
    computed: {
        items() {
            return this.record?.pregnancies || []
        },
        totalRows() {
            return this.items.length
        },
        canAdd() {
            return this.checkUserPermission('maternity_pregnancy', 'can_add')
        },
        canChange() {
            return this.checkUserPermission('maternity_pregnancy', 'can_change')
        },
        canDelete() {
            return this.checkUserPermission('maternity_pregnancy', 'can_delete')
        },
    },
    watch: {
        form: {
            deep: true,
            handler() {
                this.isDirty = this.form.dirty()
                if (this.isDirty) {
                    this.dirty()
                } else {
                    this.validated()
                }
            },
        },
    },
    methods: {
        ...mapActions('maternity', ['createPregnancy', 'updatePregnancy', 'deletePregnancy']),
        toggleAdd() {
            this.addNew = !this.addNew
            if (!this.addNew) {
                this.resetForm()
            }
        },
        onEdit(item) {
            this.editingId = item.id
            this.form.withData(item)
            this.addNew = true
        },
        onSubmit() {
            const payload = { ...this.form.data(), record: this.record.id }
            const action = this.editingId
                ? this.updatePregnancy({ ...payload, id: this.editingId })
                : this.createPregnancy(payload)

            action
                .then(() => {
                    this.notify("Saved", "Pregnancy entry saved successfully")
                    this.resetForm()
                    this.addNew = false
                })
                .catch(errors => {
                    if (errors.response) {
                        this.$refs.observer?.setErrors(errors.response.data)
                        this.hasErrors = true
                    }
                })
        },
        resetForm() {
            this.editingId = null
            this.form.reset()
            this.$nextTick(() => {
                this.$refs.observer?.reset()
                this.hasErrors = false
                this.validated()
            })
        },
        onRemove(item) {
            this.confirm = true
            this.selected = item
        },
        onConfirmed(item) {
            this.deletePregnancy(item.id)
                .then(() => {
                    this.notify("Removed", "Pregnancy entry removed successfully")
                    this.confirm = false
                    this.selected = null
                })
                .catch(() => {
                    this.notify("Error", "Something went wrong", 'danger')
                    this.confirm = false
                    this.selected = null
                })
        },
        onShown() {
            this.$refs.dialog?.focus()
        },
        onCancel() {
            this.confirm = false
            this.selected = null
        },
    },
})
</script>
